import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useUser, useUserUpadateUser } from "@/features/auth/repositories/auth-repository";
import { ProfileSchema, TProfileSchema } from "@/features/settings/forms/schema";
import { buildAccountFormData } from "@/features/settings/account-payload";

import Button from "@/components/buttons/Button";
import PhoneNumberInput from "@/components/form-controls/PhoneInput";
import SelectInput from "@/components/form-controls/SelectInput";
import TextInput from "@/components/form-controls/TextInput";
import UserAvatar from "@/components/navigation/UserAvatar";
import ChangePassword from "@/features/settings/fragments/change-password";


const salutations = [
    { label: "Mr", value: "mr" },
    { label: "Mrs", value: "mrs" },
    { label: "Sir", value: "sir" },
    { label: "Madam", value: "madam" }
]

const clientTypes = [
    { label: "Individual", value: "individual" },
    { label: "Corporate", value: "corporate" }
]


export default function Profile() {
    const { user, isLoading } = useUser();
    const account = user?.results;

    const { mutate: updateUser, isPending } = useUserUpadateUser();

    // `values` rather than `defaultValues`: the account arrives one render after the form is
    // built, and defaults are only read on the first render, which left every field empty.
    const { register, control, handleSubmit, formState: { errors } } = useForm({
        resolver: yupResolver(ProfileSchema),
        values: {
            salutation: account?.salutation ?? "",
            firstName: account?.firstName ?? "",
            lastName: account?.lastName ?? "",
            email: account?.email ?? "",
            phoneNumber: account?.phoneNumber ?? account?.mobile ?? "",
            clientType: account?.clientType ?? ""
        },
        resetOptions: { keepDirtyValues: true }
    });

    const submit = (data: TProfileSchema) => {
        if (!account) return;

        updateUser({
            id: account.id,
            input: buildAccountFormData(account, { account: data })
        });
    }

    return (
        <div className="w-full py-3 pr-3 space-y-8">
            <div>
                <h1 className="text-lg font-semibold">Profile Details</h1>
                <p className="text-xs text-secondary-300">
                    The name, contact and account type ZCT uses on your applications.
                </p>
            </div>

            <div className="flex items-center space-x-4">
                <UserAvatar
                    user={account ?? null}
                    className="w-16 h-16 text-lg"
                />

                <div className="text-sm">
                    <p className="font-medium text-secondary">
                        {[account?.firstName, account?.lastName].filter(Boolean).join(" ") || "Your account"}
                    </p>
                    <p className="text-xs text-secondary-300">
                        {account?.email}
                    </p>
                </div>
            </div>

            <form onSubmit={handleSubmit(submit)} className="grid grid-cols-2 gap-4">
                <SelectInput
                    label="Salutation"
                    displayName="label"
                    valueName="value"
                    options={salutations}
                    className="col-span-2"
                    error={errors.salutation?.message?.toString()}
                    register={register("salutation")}
                    required
                />

                <TextInput
                    label="First Name"
                    placeholder="enter your first name"
                    error={errors.firstName?.message?.toString()}
                    register={register("firstName")}
                    required
                />

                <TextInput
                    label="Last Name"
                    placeholder="enter your last name"
                    error={errors.lastName?.message?.toString()}
                    register={register("lastName")}
                    required
                />

                <TextInput
                    label="Email Address"
                    placeholder="e.g. mosha.peter@gmail.com"
                    type="email"
                    error={errors.email?.message?.toString()}
                    register={register("email")}
                    required
                />

                <PhoneNumberInput
                    name="phoneNumber"
                    label="Phone Number"
                    placeholder="enter your phone number"
                    control={control}
                    required
                />

                <SelectInput
                    label="Client Type"
                    displayName="label"
                    valueName="value"
                    options={clientTypes}
                    className="col-span-2"
                    error={errors.clientType?.message?.toString()}
                    register={register("clientType")}
                    required
                />

                <div className="col-span-2 flex justify-end">
                    <Button
                        type="submit"
                        className="min-w-32"
                        intent="primary"
                        loading={isPending}
                        disabled={isLoading || !account}
                    >
                        Save Changes
                    </Button>
                </div>
            </form>

            <ChangePassword email={account?.email} />
        </div>
    );
}
